'use client';

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContentSlot,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
  useComboboxAnchor,
} from '@zealot/ui/components/combobox';
import type { ComboboxSelectItem } from '@zealot/ui/components/combobox-select';
import { cn } from '@zealot/ui/lib/utils';
import * as React from 'react';

// gloss
type ComboboxMultiSelectProps = {
  items: ComboboxSelectItem[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  inline?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  emptyText?: string;
  loadingText?: string;
  disabled?: boolean;
  name?: string;
  maxSelected?: number;
  size?: 'sm' | 'default' | 'lg';
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  'aria-label'?: string;
};

// gloss
const ComboboxMultiSelect = ({
  items,
  value: valueProp,
  defaultValue,
  onValueChange,
  inline = false,
  isLoading = false,
  placeholder = 'Search...',
  emptyText = 'No results found.',
  loadingText = 'Loading...',
  disabled = false,
  name,
  maxSelected,
  size = 'default',
  defaultOpen,
  onOpenChange,
  className,
  'aria-label': ariaLabel,
}: ComboboxMultiSelectProps) => {
  const anchor = useComboboxAnchor();

  const itemMap = React.useMemo(() => {
    const map = new Map<string, ComboboxSelectItem>();
    for (const item of items) {
      map.set(item.value, item);
    }
    return map;
  }, [items]);

  // why: a selected value missing from the current items list must not be filtered out — doing
  // why: so silently deletes it on the next onChange emit
  const resolveItem = React.useCallback(
    (value: string): ComboboxSelectItem => itemMap.get(value) ?? { value, label: value },
    [itemMap],
  );
  const selectedItems = React.useMemo(() => valueProp?.map(resolveItem), [valueProp, resolveItem]);
  const defaultItems = React.useMemo(() => defaultValue?.map(resolveItem), [defaultValue, resolveItem]);

  const handleValueChange = React.useCallback(
    (newItems: ComboboxSelectItem[]) => {
      onValueChange?.(newItems.map((item) => item.value));
    },
    [onValueChange],
  );

  const isMaxReached = maxSelected != null && (selectedItems ?? []).length >= maxSelected;

  const currentSelection = selectedItems ?? defaultItems ?? [];

  const listBody = isLoading ? (
    <ComboboxLoading>{loadingText}</ComboboxLoading>
  ) : (
    <>
      <ComboboxEmpty>{emptyText}</ComboboxEmpty>
      <ComboboxList>
        {(item: ComboboxSelectItem) => {
          const isSelected = currentSelection.some((s) => s.value === item.value);
          return (
            <ComboboxItem key={item.value} value={item} disabled={item.disabled || (isMaxReached && !isSelected)}>
              {item.label}
            </ComboboxItem>
          );
        }}
      </ComboboxList>
    </>
  );

  return (
    <Combobox
      items={items}
      multiple
      inline={inline}
      value={selectedItems}
      defaultValue={defaultItems}
      onValueChange={handleValueChange}
      itemToStringLabel={(item: ComboboxSelectItem) => item.label}
      itemToStringValue={(item: ComboboxSelectItem) => item.value}
      name={name}
      disabled={disabled}
      defaultOpen={defaultOpen}
      onOpenChange={(open) => onOpenChange?.(open)}
    >
      <ComboboxChips ref={anchor} size={size} className={cn(className)}>
        {currentSelection.map((item) => (
          <ComboboxChip key={item.value}>{item.label}</ComboboxChip>
        ))}
        <ComboboxChipsInput
          placeholder={currentSelection.length === 0 ? placeholder : undefined}
          disabled={disabled}
          aria-label={ariaLabel}
        />
      </ComboboxChips>
      <ComboboxContentSlot inline={inline} anchor={anchor}>
        {listBody}
      </ComboboxContentSlot>
    </Combobox>
  );
};

export type { ComboboxMultiSelectProps };
export { ComboboxMultiSelect };
